import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { toast } from "react-toastify";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  clearAllGooeyToasts,
  getGooeyToastCount,
  subscribeGooeyToastCount,
} from "../utils/notificationUtils";

// When a flurry of drone detections fires multiple toasts at once, the user
// otherwise waits through N auto-closes or clicks each × in turn. This button
// surfaces a single "Clear all (N)" pill whenever any toast is up
// (VISIBLE_AT = 1 — clearing should be available immediately, not only in
// flurries) and empties BOTH toast systems instantly:
//  - the gooey detection stack, counted by notificationUtils' own registry
//    (goey-toast exposes no subscribable store, and importing sonner directly
//    crashes on a duplicate-React interop), and
//  - react-toastify, which carries the rest of the app's toasts.
// Dismissing loses nothing: the bell history is written independently before
// the toast fires, and the per-drone throttle (notifiedDroneKeys) is stamped
// at fire time, so a cleared flight does not re-toast on the next WS frame.
// gooeyToast.dismiss() with no argument also flushes goey's pending overflow
// queue, so nothing pops in after the clear.

const VISIBLE_AT = 1;

export default function ToastClearAllButton() {
  const { t } = useTranslation();
  // Live count of active react-toastify toasts, tracked via onChange
  // (status 'added' / 'updated' / 'removed') because the library doesn't
  // expose a count selector.
  const [toastifyCount, setToastifyCount] = useState(0);
  // Some adds also fire as 'updated' for the same toastId (e.g. when a
  // repeat detection hits the dedupe path that updates an existing
  // toast). Track which ids we've already counted so we don't double-
  // increment.
  const knownIds = useRef<Set<string | number>>(new Set());

  useEffect(() => {
    const unsubscribe = toast.onChange((payload) => {
      const id = payload.id;
      if (id == null) return;
      if (payload.status === "added" && !knownIds.current.has(id)) {
        knownIds.current.add(id);
        setToastifyCount((c) => c + 1);
      } else if (payload.status === "removed" && knownIds.current.has(id)) {
        knownIds.current.delete(id);
        setToastifyCount((c) => Math.max(0, c - 1));
      }
    });
    return unsubscribe;
  }, []);

  // The gooey detection stack, live from notificationUtils' registry.
  const gooeyCount = useSyncExternalStore(
    subscribeGooeyToastCount,
    getGooeyToastCount,
  );
  const activeCount = toastifyCount + gooeyCount;

  if (activeCount < VISIBLE_AT) return null;

  return (
    <button
      type="button"
      onClick={() => {
        clearAllGooeyToasts();
        toast.dismiss();
        knownIds.current.clear();
        setToastifyCount(0);
      }}
      // Header of the notification column, and BELOW the top nav rather
      // than on top of it: at top-3 this pill sat inside the nav's 48px band
      // (TopNav is h-12, and z-[9999] beats its z-[100]), covering the
      // organisation switcher — the one control in that corner. The nav ends
      // at 48px, the pill occupies 56-82, and AppLayout offsets both toasters
      // to 96 so the stack starts under it. z-[9999] still wins over the
      // toasts themselves, which overlap each other in stacked mode.
      className="fixed top-14 right-4 z-[9999] flex items-center gap-1.5 rounded-md bg-zinc-900/95 px-3 py-1.5 text-xs font-semibold text-white shadow-md backdrop-blur-sm transition-colors hover:bg-zinc-900 max-[480px]:right-2"
      aria-label={t("notifications.clearAllToasts", "Clear all notifications")}
    >
      <X className="h-3.5 w-3.5" strokeWidth={2.5} />
      {t("notifications.clearAll", "Clear all")} ({activeCount})
    </button>
  );
}
